import React, { createContext, useEffect, useState, useContext, useCallback } from 'react';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { UNIAPA_LOCAL_STORAGE_KEY, SPOTLIGHT_LOCAL_STORAGE_ITEM_KEY } from './constants.tsx';
import type {
	TouchpointContextType,
	CoordinationStopProviderProps,
	UsingNotificationsToImproveAdminApprovalProviderProps,
} from './types.tsx';

const TouchpointContext = createContext<TouchpointContextType>({
	hasSpotlightBeenShown: true,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	markSpotlightAsShown: () => {},
});

const localStorage = createLocalStorageProvider(UNIAPA_LOCAL_STORAGE_KEY);

export const CoordinationStopProvider = ({ stop, children }: CoordinationStopProviderProps) =>
	children(stop);

export const UsingNotificationsToImproveAdminApprovalProvider = ({
	children,
}: UsingNotificationsToImproveAdminApprovalProviderProps): React.JSX.Element => {
	const { cloudId, atlassianAccountId } = useTenantContext();
	const [hasSpotlightBeenShown, setHasSpotlightBeenShown] = useState<boolean>(true);

	const spotlightLocalStorageKey = `${SPOTLIGHT_LOCAL_STORAGE_ITEM_KEY}_${cloudId}_${atlassianAccountId}`;

	useEffect(() => {
		setHasSpotlightBeenShown(localStorage.get(spotlightLocalStorageKey) || false);
	}, [spotlightLocalStorageKey]);

	const markSpotlightAsShown = useCallback(() => {
		localStorage.set(spotlightLocalStorageKey, true);
		setHasSpotlightBeenShown(true);
	}, [setHasSpotlightBeenShown, spotlightLocalStorageKey]);

	return (
		<TouchpointContext.Provider
			value={{
				hasSpotlightBeenShown,
				markSpotlightAsShown,
			}}
		>
			{children}
		</TouchpointContext.Provider>
	);
};
export const useUsingNotificationsToImproveAdminApprovalContext = (): TouchpointContextType =>
	useContext(TouchpointContext);
